var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getData, utils, v2, v3 } from "@tradetrust-tt/tradetrust";
import { getDnsDidRecords } from "@tradetrust-tt/dnsprove";
import { OpenAttestationDnsDidCode } from "../../../types/error";
import { withCodedErrorHandler } from "../../../common/errorHandler";
import { CodedError } from "../../../common/error";
import { ValidDnsDidVerificationStatus, ValidDnsDidVerificationStatusArray, } from "./dnsDidProof.type";
const name = "OpenAttestationDnsDidIdentityProof";
const type = "ISSUER_IDENTITY";
const skip = () => __awaiter(void 0, void 0, void 0, function* () {
    return {
        status: "SKIPPED",
        type,
        name,
        reason: {
            code: OpenAttestationDnsDidCode.SKIPPED,
            codeString: OpenAttestationDnsDidCode[OpenAttestationDnsDidCode.SKIPPED],
            message: `Document was not issued using DNS-DID`,
        },
    };
});
const test = (document) => {
    if (utils.isSignedWrappedV2Document(document)) {
        const data = getData(document);
        return data.issuers.some((issuer) => { var _a; return ((_a = issuer.identityProof) === null || _a === void 0 ? void 0 : _a.type) === "DNS-DID"; });
    }
    else if (utils.isSignedWrappedV3Document(document)) {
        return document.openAttestationMetadata.identityProof.type === v3.IdentityProofType.DNSDid;
    }
    return false;
};
const verifyIssuerDnsDid = ({ key, location, }) => __awaiter(void 0, void 0, void 0, function* () {
    const records = yield getDnsDidRecords(location);
    return {
        location,
        key,
        status: records.some((record) => record.publicKey.toLowerCase() === key.toLowerCase()) ? "VALID" : "INVALID",
    };
});
const verifyV2 = (document) => __awaiter(void 0, void 0, void 0, function* () {
    const documentData = getData(document);
    const deferredVerificationStatus = documentData.issuers.map((issuer) => {
        const { identityProof } = issuer;
        if (!identityProof)
            throw new CodedError("Identity proof missing", OpenAttestationDnsDidCode.MALFORMED_IDENTITY_PROOF, OpenAttestationDnsDidCode[OpenAttestationDnsDidCode.MALFORMED_IDENTITY_PROOF]);
        const { key, location, type: identityProofType } = identityProof;
        if (identityProofType !== v2.IdentityProofType.DNSDid)
            throw new CodedError("Issuer is not using DID-DNS identityProof type", OpenAttestationDnsDidCode.INVALID_ISSUERS, OpenAttestationDnsDidCode[OpenAttestationDnsDidCode.INVALID_ISSUERS]);
        if (!location)
            throw new CodedError("location is not present in identity proof", OpenAttestationDnsDidCode.MALFORMED_IDENTITY_PROOF, OpenAttestationDnsDidCode[OpenAttestationDnsDidCode.MALFORMED_IDENTITY_PROOF]);
        if (!key)
            throw new CodedError("key is not present in identity proof", OpenAttestationDnsDidCode.MALFORMED_IDENTITY_PROOF, OpenAttestationDnsDidCode[OpenAttestationDnsDidCode.MALFORMED_IDENTITY_PROOF]);
        return verifyIssuerDnsDid({ key, location });
    });
    const verificationStatus = yield Promise.all(deferredVerificationStatus);
    if (ValidDnsDidVerificationStatusArray.guard(verificationStatus)) {
        return {
            name,
            type,
            data: verificationStatus,
            status: "VALID",
        };
    }
    return {
        name,
        type,
        data: verificationStatus,
        reason: {
            message: "Could not find identity at location",
            code: OpenAttestationDnsDidCode.INVALID_IDENTITY,
            codeString: "INVALID_IDENTITY",
        },
        status: "INVALID",
    };
});
const verifyV3 = (document) => __awaiter(void 0, void 0, void 0, function* () {
    if (!utils.isSignedWrappedV3Document(document))
        throw new CodedError("document is not signed", OpenAttestationDnsDidCode.UNSIGNED, OpenAttestationDnsDidCode[OpenAttestationDnsDidCode.UNSIGNED]);
    const location = document.openAttestationMetadata.identityProof.identifier;
    const { key } = document.proof;
    const verificationStatus = yield verifyIssuerDnsDid({ key, location });
    if (ValidDnsDidVerificationStatus.guard(verificationStatus)) {
        return {
            name,
            type,
            data: verificationStatus,
            status: "VALID",
        };
    }
    return {
        name,
        type,
        data: verificationStatus,
        status: "INVALID",
        reason: {
            message: "Could not find identity at location",
            code: OpenAttestationDnsDidCode.INVALID_IDENTITY,
            codeString: "INVALID_IDENTITY",
        },
    };
});
const verify = (document) => __awaiter(void 0, void 0, void 0, function* () {
    if (utils.isSignedWrappedV2Document(document))
        return verifyV2(document);
    else if (utils.isSignedWrappedV3Document(document))
        return verifyV3(document);
    throw new CodedError("Document does not match either v2 or v3 formats. Consider using `utils.diagnose` from open-attestation to find out more.", OpenAttestationDnsDidCode.UNRECOGNIZED_DOCUMENT, OpenAttestationDnsDidCode[OpenAttestationDnsDidCode.UNRECOGNIZED_DOCUMENT]);
});
export const openAttestationDnsDidIdentityProof = {
    skip,
    test,
    verify: withCodedErrorHandler(verify, {
        name,
        type,
        unexpectedErrorCode: OpenAttestationDnsDidCode.UNEXPECTED_ERROR,
        unexpectedErrorString: OpenAttestationDnsDidCode[OpenAttestationDnsDidCode.UNEXPECTED_ERROR],
    }),
};
